import { Capacitor, registerPlugin } from '@capacitor/core';

// Define the Native Plugin Interface
export interface NativeUsbPrinterPlugin {
  connect(): Promise<{ name: string }>;
  print(options: { data: number[] }): Promise<void>;
}
const NativeUsbPrinter = registerPlugin<NativeUsbPrinterPlugin>('NativeUsbPrinter');

// Web Variables (UPDATED: Now stores a cache for each printer type)
const cachedUsbDevices: Record<string, any> = { receipt: null, label: null };
const cachedUsbEndpoints: Record<string, number | null> = { receipt: null, label: null };

// Helper to keep the legacy key for receipt printers so Android users stay connected
const getAndroidUsbKey = (type: "receipt" | "label") => {
  return type === "receipt" ? 'android_usb_paired' : `android_usb_paired_${type}`;
};

// UPDATED: Added printerType argument
export async function connectUSBPrinter(userId?: string, printerType: "receipt" | "label" = "receipt"): Promise<{ device: any; endpoint: number; name?: string }> {
  const platform = Capacitor.getPlatform();

  // ─── ANDROID NATIVE LOGIC ───
  if (platform === 'android') {
    const result = await NativeUsbPrinter.connect();
    localStorage.setItem(getAndroidUsbKey(printerType), 'true');
    return { device: "NATIVE_USB", endpoint: 0, name: result.name };
  }

  // ─── WEB / DESKTOP LOGIC ───
  if (!(navigator as any).usb) {
    throw new Error("USB Printing is not supported on this browser.");
  }
  const usb = (navigator as any).usb;
  const device = await usb.requestDevice({ filters: [] });
  await device.open();
  if (device.configuration === null) await device.selectConfiguration(1);

  for (const iface of device.configuration.interfaces) {
    try {
      await device.claimInterface(iface.interfaceNumber);
      for (const alt of iface.alternates) {
        for (const ep of alt.endpoints) {
          if (ep.direction === "out") {
            cachedUsbDevices[printerType] = device;
            cachedUsbEndpoints[printerType] = ep.endpointNumber;
            return { device, endpoint: ep.endpointNumber, name: device.productName };
          }
        }
      }
    } catch (_) { }
  }
  await device.close();
  throw new Error("No writable USB endpoint found.");
}

// NOTE: sendToUSBPrinter doesn't need printerType because the device object is passed directly into it.
export async function sendToUSBPrinter(
  device: any,
  endpoint: number,
  data: Uint8Array,
  userId?: string
): Promise<void> {
  const platform = Capacitor.getPlatform();

  // ─── ANDROID NATIVE LOGIC ───
  if (platform === 'android') {
    const dataArray = Array.from(data);
    await NativeUsbPrinter.print({ data: dataArray });
    return;
  }

  // ─── WEB / DESKTOP LOGIC ───
  const chunkSize = 512;
  for (let i = 0; i < data.length; i += chunkSize) {
    await device.transferOut(endpoint, data.slice(i, i + chunkSize));
    await new Promise((r) => setTimeout(r, 10));
  }
}

// UPDATED: Added printerType argument
export function getCachedUSBPrinter(userId?: string, printerType: "receipt" | "label" = "receipt"): { device: any; endpoint: number; name?: string } | null {
  const platform = Capacitor.getPlatform();

  // ─── ANDROID CACHE ───
  if (platform === 'android') {
    const isPaired = localStorage.getItem(getAndroidUsbKey(printerType));
    if (isPaired === 'true') {
      return { device: "NATIVE_USB", endpoint: 0, name: "USB Printer" };
    }
    return null;
  }

  // ─── WEB CACHE ───
  if (cachedUsbDevices[printerType] && cachedUsbEndpoints[printerType] !== null) {
    return { device: cachedUsbDevices[printerType], endpoint: cachedUsbEndpoints[printerType]!, name: "USB Printer" };
  }
  return null;
}

// UPDATED: Added printerType argument
export function clearUSBPrinterCache(userId?: string, printerType: "receipt" | "label" = "receipt"): void {
  localStorage.removeItem(getAndroidUsbKey(printerType));
  cachedUsbDevices[printerType] = null;
  cachedUsbEndpoints[printerType] = null;
}